import {
  arrayToString,
  buildRegex,
  createFormat,
  RegexObject,
  RegexAdditionObject,
  RegexFormat,
} from './helpers';

/**
 * List of date unit format objects to build out regex and formatStrings.
 */
const dateUnits = {
  mb: createFormat(/^\d$/, 'M'),
  mmb: createFormat(/^[0-1]\d$/, 'MM'),
  mmmmb: createFormat(/^\w*$/, 'MMMM'),
  db: createFormat(/^\d$/, 'D'),
  ddb: createFormat(/^[0-3]\d$/, 'DD'),
  yyb: createFormat(/^(\d\d)$/, 'YY'),
  yyyyb: createFormat(/^([1-2]\d\d\d)$/, 'YYYY'),
  m: createFormat('\\d', 'M'),
  mm: createFormat('[0-1]\\d', 'MM'),
  mmmm: createFormat('\\w*', 'MMMM'),
  d: createFormat('\\d', 'D'),
  dd: createFormat('[0-3]\\d', 'DD'),
  yy: createFormat('\\d\\d', 'YY'),
  yyyy: createFormat('[1-2]\\d\\d\\d', 'YYYY'), // This will prevent input year < 1000 and > 3000.
  sl: createFormat('[/.-]', '/'),
  sp: createFormat('\\s', ' '),
  cm: createFormat('[,]', ','),
};

type DateFormatTuple = [RegexObject, ...RegexAdditionObject[]];
/**
 * Create a list of date format objects.
 * @returns { Array } List of date formats [{ regex, formatString }]
 */
const getDateFormatList = () => {
  const { mb, mmb, mmmmb, db, ddb, yyb, yyyyb, m, mm, mmmm, d, dd, yy, yyyy, sl, sp, cm } =
    dateUnits;

  return [
    [mb, sl, d, sl, yy] as DateFormatTuple,
    [mmb, sl, d, sl, yy] as DateFormatTuple,
    [mb, sl, dd, sl, yy] as DateFormatTuple,
    [mmb, sl, dd, sl, yy] as DateFormatTuple,
    [mb, sl, d, sl, yyyy] as DateFormatTuple,
    [mmb, sl, d, sl, yyyy] as DateFormatTuple,
    [mb, sl, dd, sl, yyyy] as DateFormatTuple,
    [mmb, sl, dd, sl, yyyy] as DateFormatTuple,
    [mmmmb, sp, d, sp, yyyy] as DateFormatTuple,
    [mmmmb, sp, dd, sp, yyyy] as DateFormatTuple,
    [mmmmb, sp, d, cm, sp, yyyy] as DateFormatTuple,
    [mmmmb, sp, dd, cm, sp, yyyy] as DateFormatTuple,
    [yyb, sl, m, sl, d] as DateFormatTuple,
    [yyb, sl, mm, sl, d] as DateFormatTuple,
    [yyb, sl, m, sl, dd] as DateFormatTuple,
    [yyb, sl, mm, sl, dd] as DateFormatTuple,
    [yyyyb, sl, m, sl, d] as DateFormatTuple,
    [yyyyb, sl, mm, sl, d] as DateFormatTuple,
    [yyyyb, sl, m, sl, dd] as DateFormatTuple,
    [yyyyb, sl, mm, sl, dd] as DateFormatTuple,
    [yyb, sp, mmmm, sp, d] as DateFormatTuple,
    [yyb, sp, mmmm, sp, dd] as DateFormatTuple,
    [yyyyb, sp, mmmm, sp, d] as DateFormatTuple,
    [yyyyb, sp, mmmm, sp, dd] as DateFormatTuple,
    [db, sp, mmmm, sp, yy] as DateFormatTuple,
    [ddb, sp, mmmm, sp, yy] as DateFormatTuple,
    [db, sp, mmmm, sp, yyyy] as DateFormatTuple,
    [ddb, sp, mmmm, sp, yyyy] as DateFormatTuple,
  ];
};

/**
 * Creates an array of date formats.
 * @returns { Array } Date formats.
 */
const createDateFormats = () => {
  const dateFormats: RegexFormat[] = [];
  getDateFormatList().forEach((format) => {
    const regex = buildRegex(format[0], format.slice(1));
    const formatString = arrayToString(format, 'formatString');
    const dateFormat = createFormat(regex, formatString);
    dateFormats.push(dateFormat);
  });
  return dateFormats;
};

export const dateFormatList = createDateFormats();

/**
 * getDateFormat
 * Get the date format from a given date input if it matches regex
 * @param { String } date: Date to test.
 * @returns { Object } Date format Object { regex, formatString }.
 */
export function getDateFormat(date: string): RegexFormat | Record<string, never> {
  let dateFormat = {};
  dateFormatList.forEach((format) => {
    if (format.regex && format.regex.test(date)) {
      dateFormat = format;
    }
  });
  return dateFormat;
}
